import { envConfig } from '#constants/envConfig';

export type NativeVoiceActivity = {
  kind?: 'session' | 'call';
  /** Downloaded and reduced by the app process, never by the widget extension. */
  avatarURL?: string | null;
  gameIconURL?: string | null;
  gameWallpaperURL?: string | null;
  sessionId: string;
  sessionPostId?: string | null;
  voiceMode?: string | null;
  canJoinVoice?: boolean;
  chatId: string | null;
  unreadCount: number;
  gameName: string;
  sessionName: string;
  startedAt: number | null;
  expiresAt: number | null;
  phase: string;
  muted: boolean;
  canToggle: boolean;
  micDenied?: boolean;
  revision: number;
  participants: {
    userId?: string;
    initial: string;
    initialColor?: string;
    inVoice: boolean;
    avatarURL?: string | null;
  }[];
  total: number;
  copy: {
    chat: string;
    unreadMessages: string;
    duration: string;
    waiting: string;
    composition: string;
    reconnecting: string;
    stale: string;
    micOn: string;
    micOff: string;
    micEnabling?: string;
    micUnknown?: string;
    micDenied?: string;
    micUnavailable: string;
    mute: string;
    unmute: string;
    openMic: string;
    joinVoice?: string;
    openSession?: string;
    sessionEnded?: string;
    open?: string;
    rosterStale?: string;
    voiceUnknown?: string;
    unreadCountTemplate?: string;
    voiceCountTemplate?: string;
    platformSession?: string;
    noVoiceSession?: string;
    inGameSession?: string;
    discordSession?: string;
  };
};

/** Prevents a call id and a gaming-session id from sharing a microphone intent. */
export const nativeCallActivityId = (callId: string) => `call:${callId}`;

export type NativeActivityStatus = {
  activityStatus?: 'active' | 'deferred' | 'disabled' | 'failed' | 'ended';
  authorizationEnabled?: boolean;
  payloadBytes?: number;
  errorDomain?: string;
  errorCode?: number;
};
export type NativeMicState = NativeActivityStatus & {
  sessionId: string;
  muted: boolean;
  revision: number;
};
export type NativeMicRequest = NativeMicState & { requestId: string };
export type NativeVoiceActivityDiagnostics = {
  schemaVersion: number;
  available: boolean;
  events: Record<string, string | number | boolean>[];
  latest: Record<string, unknown>;
};
type Handle = { remove(): Promise<void> };
interface VoiceLiveActivityPlugin {
  update(content: NativeVoiceActivity): Promise<NativeMicState | undefined>;
  stop(): Promise<void>;
  syncAuth(): Promise<void>;
  getDiagnostics(): Promise<NativeVoiceActivityDiagnostics>;
  completeMicrophone(options: {
    requestId: string;
    success: boolean;
  }): Promise<{ accepted: boolean }>;
  addListener(
    event: 'microphoneRequest',
    listener: (request: NativeMicRequest) => void
  ): Promise<Handle>;
  addListener(
    event: 'activityStatus',
    listener: (status: NativeActivityStatus) => void
  ): Promise<Handle>;
}

let plugin: VoiceLiveActivityPlugin | null | undefined;
let loading: Promise<void> | undefined;
async function ensurePlugin() {
  if (!envConfig.IS_NATIVE || plugin !== undefined) return;
  loading ??= (async () => {
    const { Capacitor, registerPlugin } = await import('@capacitor/core');
    plugin =
      Capacitor.getPlatform() === 'ios' && Capacitor.isPluginAvailable('VoiceLiveActivity')
        ? registerPlugin<VoiceLiveActivityPlugin>('VoiceLiveActivity')
        : null;
  })().catch(() => {
    plugin = null;
  });
  await loading;
}

export async function updateNativeVoiceActivity(content: NativeVoiceActivity) {
  await ensurePlugin();
  return plugin?.update(content);
}
export async function stopNativeVoiceActivity() {
  await ensurePlugin();
  await plugin?.stop();
}
/** Support evidence only: an APNs acceptance is separate from app observation. */
export async function readNativeVoiceActivityDiagnostics() {
  await ensurePlugin();
  if (!plugin) return null;
  try { return await plugin.getDiagnostics(); }
  catch { return null; } // Older installed shells don't expose this method.
}
export async function onNativeVoiceActivityStatus(
  listener: (status: NativeActivityStatus) => void
) {
  await ensurePlugin();
  const handle = await plugin?.addListener('activityStatus', listener);
  return () => {
    void handle?.remove();
  };
}
export async function onNativeVoiceMicRequest(listener: (request: NativeMicRequest) => void) {
  await ensurePlugin();
  const handle = await plugin?.addListener('microphoneRequest', listener);
  return () => {
    void handle?.remove();
  };
}
export async function completeNativeVoiceMic(requestId: string, success: boolean) {
  await ensurePlugin();
  return (await plugin?.completeMicrophone({ requestId, success }))?.accepted ?? false;
}

/** Native Preferences already contains the verified credential; never pass it through this bridge. */
export async function syncNativeVoiceActivityAuth() {
  await ensurePlugin();
  await plugin?.syncAuth();
}
